/* eslint-disable react/display-name */
/**
 * @file ManagementViewGenerator.jsx
 * @author Fernando Ruano
 * @description ManagementViewGenerator Component
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import TableFilter from 'components/TableFilter';

// Hooks
import useSearch from '@nephro/hook/useSearch';

// Styles
import { useDispatch, useSelector } from 'react-redux';
import { setResponse, setTableConfig } from 'redux/stores/app/actions';
import styles from './styles.module.css';

const iconSearch = (
  <SearchOutlined
    style={{
      fontSize: 16,
      color: 'var(--color-grayFontInput)'
    }}
  />
);

const menu = [
  { key: 0, value: '1st menu item' },
  { key: 1, value: '2nd menu item' },
  { key: 2, value: '3rd menu item' }
];

const ManagementViewGenerator = ({
  tableHeader,
  search,
  filter,
  rightOptions,
  columns,
  data,
  hasRowSelection,
  isVirtualPagination,
  pagination,
  onChange,
  onChangePagination,
  onRowSelection,
  withPageSize
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const { searchTerm, setSearchTerm, filteredRows } = useSearch(data);
  // eslint-disable-next-line no-underscore-dangle
  const tableState = useSelector((state) =>
    state.getIn(['AppState', 'tableConfig'])
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    onRowSelection(newSelectedRowKeys);
  };

  const customSelectionRowsClient = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
    onSelectNone: () => {}
  };

  const listOfRows = filteredRows.map((item, index) => {
    return {
      key: index,
      ...item
    };
  });

  useEffect(() => {
    if (tableState?.resetSelectedRows) {
      setSelectedRowKeys([]);
      dispatch(
        setResponse(
          'success',
          'sample',
          'Proceso de Invitacion Masiva en ejecucion. Recibira un correo de confirmacion cuando termine.',
          {},
          () => {}
        )
      );
      dispatch(setTableConfig({ resetSelectedRows: false }));
    }
  }, [tableState?.resetSelectedRows]);

  return (
    <div className="h-full">
      {tableHeader && (
        <div className="px-3 md:px-7 py-4  flex flex-col-reverse smd:flex-row justify-between w-full">
          <div className="mr-2 mt-5 smd:mt-0 flex flex-row items-center w-full">
            {search && (
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={iconSearch}
                placeholder={translate('commons.search')}
                allowClear
                className={styles.inputSearch}
              />
            )}
            {filter && (
              <TableFilter menuItems={menu} onSelectItem={() => null} />
            )}
          </div>
          {rightOptions.length > 0 && (
            <div className="flex flex-row items-center">
              {rightOptions.map((item) => item)}
            </div>
          )}
        </div>
      )}
      <Table
        bordered={false}
        columns={columns}
        dataSource={listOfRows}
        size="small"
        scroll={{ x: true }}
        rowKey={(record, i) => `${record.documento}-${i}`}
        rowSelection={hasRowSelection ? customSelectionRowsClient : null}
        onChange={isVirtualPagination ? onChangePagination : onChange}
        pagination={
          isVirtualPagination
            ? {
                position: ['bottomCenter'],
                showSizeChanger: withPageSize,
                hideOnSinglePage: true,
                total: filteredRows.length,
                current: pagination?.current + 1,
                pageSizeOptions: [10, 50, 100, 500, 1000]
              }
            : false
        }
      />
    </div>
  );
};

/**
 * @name ManagementViewGenerator.propTypes
 * @type { propTypes } React PropTypes.
 * @property { Boolean } tableHeader - To show table header options.
 * @property { Boolean } search - To show search input.
 * @property { Boolean } filter - To show filter input.
 * @property { Array } rightOptions - Right options array.
 * @property { Array } columns - Table columns.
 * @property { Array } data - Table rows data.
 */
ManagementViewGenerator.propTypes = {
  tableHeader: PropTypes.bool,
  search: PropTypes.bool,
  filter: PropTypes.bool,
  rightOptions: PropTypes.array,
  columns: PropTypes.array,
  data: PropTypes.array,
  pagination: PropTypes.shape({
    total: PropTypes.number,
    current: PropTypes.number,
    isVirtualPagination: PropTypes.bool
  }),
  hasRowSelection: PropTypes.bool,
  isVirtualPagination: PropTypes.bool,
  onChange: PropTypes.func,
  onChangePagination: PropTypes.func,
  onRowSelection: PropTypes.func,
  withPageSize: PropTypes.bool
};

/**
 * Initialize default props
 */
ManagementViewGenerator.defaultProps = {
  tableHeader: true,
  search: true,
  filter: true,
  rightOptions: [],
  columns: [],
  data: [],
  onChange: () => {},
  onRowSelection: () => {},
  withPageSize: false
};

export default ManagementViewGenerator;
